import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowRight, Shield, BookOpen, Users, Calendar, Lock, HelpCircle, Landmark } from 'lucide-react';

/**
 * SCHOOLS LANDING PAGE
 * 
 * Entry point for schools, homeschool groups, micro-schools, and educational programs.
 * 
 * Structure:
 * 1. What it is
 * 2. Who it's for
 * 3. How it works (3 bullets)
 * 4. Safety promise
 * 5. Primary CTA
 * 6. Secondary CTA to FAQ
 */
export default function SchoolsLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-sm" style={{ backgroundColor: 'rgba(244, 239, 230, 0.95)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" 
                alt="LikeKind" 
                className="w-8 h-8 rounded-lg"
              />
              <span className="font-bold" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>LikeKind</span>
            </button>
            <Button 
              onClick={() => navigate('/institutions/create?type=school')}
              size="sm"
              className="rounded-full"
              style={{ backgroundColor: 'var(--lk-earth-brown)', color: 'var(--lk-warm-sand)' }}
              data-testid="schools-enter-btn"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-12">
        
        {/* What It Is */}
        <section className="mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
            LikeKind for Schools
          </h1>
          <p className="text-lg leading-relaxed mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            A simple place to coordinate schedules, announcements, and materials — without feeds, ads, or tracking.
          </p>
          <p className="text-base leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
            LikeKind gives schools and educational programs a structured way to communicate with families and students 
            while maintaining clear boundaries and strong safety rules.
          </p>
        </section>

        {/* What This Is NOT */}
        <section className="mb-12 p-6 rounded-2xl" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
          <h2 className="font-semibold mb-3" style={{ color: 'var(--fy-text-primary)' }}>This is not:</h2>
          <ul className="space-y-2 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--lk-earth-brown)' }} />
              A social network
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--lk-earth-brown)' }} />
              An engagement platform
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--lk-earth-brown)' }} />
              Designed to capture attention
            </li>
          </ul>
        </section>

        {/* What Schools Use It For */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold mb-6" style={{ color: 'var(--fy-text-primary)' }}>What schools use LikeKind for</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(139, 106, 79, 0.15)' }}>
                <Calendar className="w-4 h-4" style={{ color: 'var(--lk-earth-brown)' }} />
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  Calendars, closures, and schedule coordination
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(139, 106, 79, 0.15)' }}>
                <BookOpen className="w-4 h-4" style={{ color: 'var(--lk-earth-brown)' }} />
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  Announcements and updates
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(139, 106, 79, 0.15)' }}>
                <Landmark className="w-4 h-4" style={{ color: 'var(--lk-earth-brown)' }} />
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  Attachments (notes, permission slips, documents)
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(139, 106, 79, 0.15)' }}>
                <Users className="w-4 h-4" style={{ color: 'var(--lk-earth-brown)' }} />
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  Teacher → student communication
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(139, 106, 79, 0.15)' }}>
                <Shield className="w-4 h-4" style={{ color: 'var(--lk-earth-brown)' }} />
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  Parent read-only visibility where applicable
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What LikeKind Does NOT Do */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>What LikeKind does not do</h2>
          <ul className="space-y-2 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            <li className="flex items-center gap-2">
              <Lock className="w-4 h-4" style={{ color: 'var(--lk-primary-green)' }} />
              No student discovery or public profiles
            </li>
            <li className="flex items-center gap-2">
              <Lock className="w-4 h-4" style={{ color: 'var(--lk-primary-green)' }} />
              No student-to-student messaging
            </li>
            <li className="flex items-center gap-2">
              <Lock className="w-4 h-4" style={{ color: 'var(--lk-primary-green)' }} />
              No advertising or sponsorship targeting
            </li>
            <li className="flex items-center gap-2">
              <Lock className="w-4 h-4" style={{ color: 'var(--lk-primary-green)' }} />
              No data resale or behavior tracking
            </li>
            <li className="flex items-center gap-2">
              <Lock className="w-4 h-4" style={{ color: 'var(--lk-primary-green)' }} />
              No engagement metrics or ranking
            </li>
          </ul>
        </section>

        {/* Student Safety by Design */}
        <section className="mb-12 p-6 rounded-2xl" style={{ backgroundColor: 'var(--lk-sage-muted)', border: '1px solid var(--lk-primary-green)' }}>
          <div className="flex items-center gap-3 mb-3">
            <Shield className="w-5 h-5" style={{ color: 'var(--lk-primary-green)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--lk-primary-green)' }}>Student Safety by Design</h2>
          </div>
          <ul className="text-sm space-y-2" style={{ color: 'var(--fy-text-primary)' }}>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>Students are not searchable</span>
            </li>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>Messaging is role-restricted</span>
            </li>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>Public content is policy-enforced</span>
            </li>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>Violations trigger automatic enforcement</span>
            </li>
          </ul>
          <p className="text-sm font-medium mt-4" style={{ color: 'var(--lk-primary-green)' }}>
            Safety features are not upgrades. They are built in.
          </p>
        </section>

        {/* How Schools Join */}
        <section className="mb-12 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
          <h3 className="font-semibold mb-3" style={{ color: 'var(--fy-text-primary)' }}>How schools join</h3>
          <p className="text-sm mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
            Schools and programs create a Commons, which acts as the coordination space for calendars, announcements, and materials.
          </p>
          <p className="text-sm mb-2" style={{ color: 'var(--fy-text-secondary)' }}>
            Setup includes:
          </p>
          <ul className="text-sm space-y-1 ml-4" style={{ color: 'var(--fy-text-secondary)' }}>
            <li>• Steward verification</li>
            <li>• Policy acknowledgment</li>
            <li>• Safety rules acceptance</li>
          </ul>
          <p className="text-sm mt-3" style={{ color: 'var(--fy-text-disabled)' }}>
            Once active, families and students are invited under controlled access.
          </p>
        </section>

        {/* Transparency */}
        <section className="mb-12 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
          <h3 className="font-semibold mb-2" style={{ color: 'var(--fy-text-primary)' }}>Transparency</h3>
          <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            LikeKind does not optimize for attention. It does not sell data. It does not use ads.
          </p>
          <p className="text-sm mt-2" style={{ color: 'var(--fy-text-secondary)' }}>
            Privacy is not a promise here — it is how the system is built.
          </p>
        </section>

        {/* CTAs */}
        <section className="space-y-4">
          <Button 
            onClick={() => navigate('/membership')}
            className="w-full h-14 rounded-xl text-lg font-semibold"
            style={{ backgroundColor: 'var(--lk-earth-brown)', color: 'var(--lk-warm-sand)' }}
            data-testid="schools-create-btn"
          >
            Get Started
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-xs text-center" style={{ color: 'var(--fy-text-disabled)' }}>
            Select &quot;School / Institution / Organization&quot; at entry.
          </p>
          <Button 
            onClick={() => navigate('/faq/schools')}
            variant="outline"
            className="w-full h-12 rounded-xl font-medium"
            style={{ borderColor: 'var(--fy-border-subtle)', color: 'var(--fy-text-secondary)' }}
            data-testid="schools-faq-btn"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            School Safety FAQ
          </Button>
        </section>

        {/* Footer Links */}
        <footer className="mt-16 pt-8 flex flex-wrap gap-4 justify-center text-sm" style={{ borderTop: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-disabled)' }}>
          <button onClick={() => navigate('/for/individuals')} className="hover:underline">For Individuals</button>
          <span>·</span>
          <button onClick={() => navigate('/for/families')} className="hover:underline">For Families</button>
          <span>·</span>
          <button onClick={() => navigate('/for/institutions')} className="hover:underline">For Institutions</button>
          <span>·</span>
          <button onClick={() => navigate('/privacy')} className="hover:underline">Privacy</button>
          <span>·</span>
          <button onClick={() => navigate('/terms')} className="hover:underline">Terms</button>
        </footer>
      </main>
    </div>
  );
}
